/**
 * Text Block Widget (Story 5.5, Task 7 → updated Story 5.7, Task 2)
 *
 * Renders free-form text content with variant-based styling.
 * Content renders as plain text (no raw HTML for XSS prevention).
 * The ai-summary variant is for internal code differentiation only —
 * no visible "AI" label in the output.
 *
 * Auto-resize: uses useAutoResize hook as a safety net for content overflow.
 * When content exceeds 8 rowSpan, overflow-y: auto enables scrolling.
 */

import type { WidgetConfig } from "../../../lib/templateUtils";
import { useAutoResize } from "../../../lib/useAutoResize";

interface TextBlockWidgetProps {
  widget: WidgetConfig;
  onAutoResize?: (widgetId: string, newRowSpan: number) => void;
}

export function TextBlockWidget({ widget, onAutoResize }: TextBlockWidgetProps) {
  const config = widget.config as { content?: string; variant?: "heading" | "body" | "ai-summary" };
  const content = config.content ?? "";
  const variant = config.variant ?? "body";
  const currentRowSpan = widget.position.rowSpan ?? 1;
  const isAutoSized = widget.autoSize !== false;

  const containerRef = useAutoResize({
    currentRowSpan,
    enabled: isAutoSized && !!content && !!onAutoResize,
    onAutoResize: (newRowSpan) => onAutoResize?.(widget.id, newRowSpan),
  });

  // Empty content state
  if (!content) {
    return (
      <div data-testid="widget-type-text-block" className="flex h-full flex-col p-4">
        <h3 className="mb-2 truncate text-[13px] font-medium uppercase tracking-wide text-muted-foreground">
          {widget.title}
        </h3>
        <div className="flex flex-1 items-center justify-center">
          <span className="text-[13.5px] italic text-muted-foreground" data-testid="text-block-empty">
            Add content...
          </span>
        </div>
      </div>
    );
  }

  const variantClasses: Record<string, string> = {
    heading: "text-xl font-bold leading-tight text-foreground",
    body: "text-[13.5px] leading-relaxed text-foreground",
    "ai-summary": "text-[13.5px] leading-relaxed italic border-l-2 border-border pl-3 text-muted-foreground",
  };

  // Cap at 8 rowSpan — excess content scrolls
  const overflowClass = currentRowSpan >= 8 ? "overflow-y-auto" : "";

  return (
    <div data-testid="widget-type-text-block" className="flex h-full flex-col p-4">
      <h3 className="mb-2 truncate text-[13px] font-medium uppercase tracking-wide text-muted-foreground">
        {widget.title}
      </h3>
      <div ref={containerRef} className={`min-w-0 flex-1 ${overflowClass}`}>
        {variant === "heading" ? (
          <h2 className={variantClasses[variant]} data-testid="text-block-content">
            {content}
          </h2>
        ) : (
          <p className={variantClasses[variant]} data-testid="text-block-content">
            {content}
          </p>
        )}
      </div>
    </div>
  );
}
